import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Link,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { googleMapsPlaceUrl } from '../../lib/googleMapsRuntime';
import {
  distanceMeters,
  formatMeters,
  SITE_COVERAGE_RADIUS_M,
  type SiteViewpoint,
} from '../../lib/employerSiteViewpoints';
import { buildImageryDateFlag } from '../../lib/employerSiteEvidenceFlags';
import {
  geocodeAddressCached,
  isAmbiguousGeocode,
  type GeocodeCandidate,
} from '../../lib/googleMapsGeocode';
import { EmployerSiteEvidenceBulletin } from './EmployerSiteEvidenceBulletin';
import { useEmployerSiteViewpoints } from './useEmployerSiteViewpoints';
import { EmployerSiteAerialStage } from './EmployerSiteAerialStage';
import { EmployerSiteStreetStage } from './EmployerSiteStreetStage';
import { EmployerSite3DStage } from './EmployerSite3DStage';

type StageKey = 'street' | 'aerial' | 'threeD';

type Props = {
  open: boolean;
  onClose: () => void;
  employer: {
    name: string;
    address: string | null;
    latitude: number;
    longitude: number;
  };
};

const EMPTY_VIEWPOINTS: SiteViewpoint[] = [];

function buildingKeyOf(lat: number, lng: number): string {
  return `${lat.toFixed(6)},${lng.toFixed(6)}`;
}

export function EmployerSiteInspector({ open, onClose, employer }: Props) {
  const employerBuilding = useMemo(
    () => ({ lat: employer.latitude, lng: employer.longitude }),
    [employer.latitude, employer.longitude],
  );

  const [building, setBuilding] = useState(employerBuilding);
  const [displayAddress, setDisplayAddress] = useState(
    employer.address ?? `${employerBuilding.lat.toFixed(5)}, ${employerBuilding.lng.toFixed(5)}`,
  );
  const [locationLabel, setLocationLabel] = useState(employer.name);
  const [operatorRelocated, setOperatorRelocated] = useState(false);
  const [addressQuery, setAddressQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchCandidates, setSearchCandidates] = useState<GeocodeCandidate[]>([]);
  const [stage, setStage] = useState<StageKey>('street');
  const [visited, setVisited] = useState<StageKey[]>(['street']);
  const [activePanoId, setActivePanoId] = useState<string | null>(null);
  const searchGenerationRef = useRef(0);
  const wasOpenRef = useRef(false);

  const { status, viewpoints, error, buildingKey } = useEmployerSiteViewpoints({
    building,
    enabled: open,
  });

  const currentBuildingKey = buildingKeyOf(building.lat, building.lng);
  const viewpointsForBuilding = useMemo(() => {
    if (
      buildingKey === currentBuildingKey &&
      (status === 'ready' || status === 'none' || status === 'error')
    ) {
      return viewpoints;
    }
    return EMPTY_VIEWPOINTS;
  }, [buildingKey, currentBuildingKey, status, viewpoints]);
  const viewpointsStatus =
    buildingKey === currentBuildingKey
      ? status
      : open
        ? 'loading'
        : 'idle';

  // Reset UI when the dialog opens (or employer target changes while open).
  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      searchGenerationRef.current += 1;
      setSearching(false);
      return;
    }

    const justOpened = !wasOpenRef.current;
    wasOpenRef.current = true;

    setBuilding(employerBuilding);
    setDisplayAddress(
      employer.address ?? `${employerBuilding.lat.toFixed(5)}, ${employerBuilding.lng.toFixed(5)}`,
    );
    setLocationLabel(employer.name);
    setOperatorRelocated(false);
    setAddressQuery('');
    setSearchError(null);
    setSearchCandidates([]);
    setSearching(false);
    setActivePanoId(null);
    if (justOpened) {
      setStage('street');
      setVisited(['street']);
    }
  }, [open, employer.name, employer.address, employerBuilding]);

  useEffect(() => {
    setActivePanoId(null);
  }, [building.lat, building.lng]);

  useEffect(() => {
    if (!open) return;
    if (viewpointsStatus !== 'ready' || viewpointsForBuilding.length === 0) return;
    setActivePanoId((current) => {
      if (current && viewpointsForBuilding.some((v) => v.panoId === current)) return current;
      return viewpointsForBuilding[0]?.panoId ?? null;
    });
  }, [open, viewpointsStatus, viewpointsForBuilding]);

  useEffect(() => {
    setVisited((seen) => (seen.includes(stage) ? seen : [...seen, stage]));
  }, [stage]);

  const activeViewpoint =
    viewpointsForBuilding.find((v) => v.panoId === activePanoId) ?? null;

  const imageryDateFlag = useMemo(
    () => buildImageryDateFlag({ imageDateRaw: activeViewpoint?.imageDate }),
    [activeViewpoint?.imageDate],
  );

  const openViewpoint = (panoId: string) => {
    setActivePanoId(panoId);
    setStage('street');
  };

  const applyCandidate = (match: GeocodeCandidate) => {
    setBuilding(match.location);
    setDisplayAddress(match.formattedAddress);
    setLocationLabel('Searched location');
    setOperatorRelocated(true);
    setStage('aerial');
    setSearchCandidates([]);
    setAddressQuery('');
    setSearchError(null);
  };

  const searchAddress = async (event: FormEvent) => {
    event.preventDefault();
    const address = addressQuery.trim();
    if (!address || searching) return;

    const generation = ++searchGenerationRef.current;
    setSearching(true);
    setSearchError(null);
    setSearchCandidates([]);

    try {
      const result = await geocodeAddressCached({
        address,
        country: 'CA',
        bias: building,
      });

      if (generation !== searchGenerationRef.current) return;

      if (!result.ok) {
        setSearchError(result.error);
        return;
      }

      const candidates = result.candidates.slice(0, 5);
      if (candidates.length === 0) {
        setSearchError('No matching address found. Add a city, province, or postal code.');
        return;
      }

      if (isAmbiguousGeocode(candidates) || candidates.length > 1) {
        setSearchCandidates(candidates);
        setSearchError(null);
        return;
      }

      applyCandidate(candidates[0]!);
    } catch (err) {
      if (generation !== searchGenerationRef.current) return;
      setSearchError(
        err instanceof Error ? err.message : 'Address search failed. Check the address and try again.',
      );
    } finally {
      if (generation === searchGenerationRef.current) setSearching(false);
    }
  };

  const resetToEmployer = () => {
    searchGenerationRef.current += 1;
    setSearching(false);
    setBuilding(employerBuilding);
    setDisplayAddress(
      employer.address ?? `${employerBuilding.lat.toFixed(5)}, ${employerBuilding.lng.toFixed(5)}`,
    );
    setLocationLabel(employer.name);
    setOperatorRelocated(false);
    setAddressQuery('');
    setSearchError(null);
    setSearchCandidates([]);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      slotProps={{ paper: { sx: { height: '92vh', borderRadius: 2 } } }}
      data-testid="employer-site-inspector"
    >
      <EmployerSiteEvidenceBulletin flag={imageryDateFlag} />

      <Box sx={{ px: 2, pt: 1.5, pb: 1.25 }}>
        <Stack direction="row" alignItems="flex-start" spacing={1.5}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              Site inspection · {employer.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {displayAddress}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.35, fontWeight: 600, lineHeight: 1.35 }}
            >
              Imagery can show a building at these coordinates. It cannot confirm the applicant works
              here, the current tenant, or the unit.
            </Typography>
          </Box>
          <Link
            href={googleMapsPlaceUrl(building)}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{ fontWeight: 700, fontSize: '0.8rem', whiteSpace: 'nowrap', mt: 0.5 }}
          >
            Google Maps <LaunchRoundedIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} />
          </Link>
          <IconButton size="small" onClick={onClose} aria-label="Close site inspection">
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Stack
          component="form"
          onSubmit={searchAddress}
          direction="row"
          spacing={0.75}
          alignItems="flex-start"
          sx={{ mt: 1.25 }}
        >
          <TextField
            size="small"
            fullWidth
            value={addressQuery}
            onChange={(event) => {
              setAddressQuery(event.target.value);
              setSearchCandidates([]);
            }}
            placeholder="Search another employer address (Canada)"
            error={Boolean(searchError)}
            helperText={searchError}
            inputProps={{ 'aria-label': 'Search employer address' }}
            InputProps={{
              startAdornment: <SearchRoundedIcon color="action" sx={{ mr: 0.75, fontSize: 20 }} />,
            }}
            sx={{ maxWidth: 560 }}
          />
          <Button
            type="submit"
            size="small"
            variant="contained"
            disabled={!addressQuery.trim() || searching}
            sx={{ minHeight: 40, textTransform: 'none', fontWeight: 700 }}
          >
            {searching ? <CircularProgress size={18} color="inherit" /> : 'Point on map'}
          </Button>
          {locationLabel === 'Searched location' ? (
            <Button
              size="small"
              variant="text"
              startIcon={<RestartAltRoundedIcon />}
              onClick={resetToEmployer}
              sx={{ minHeight: 40, whiteSpace: 'nowrap', textTransform: 'none' }}
            >
              Employer address
            </Button>
          ) : null}
        </Stack>

        {operatorRelocated ? (
          <Alert
            severity="warning"
            variant="outlined"
            icon={<WarningAmberRoundedIcon fontSize="inherit" />}
            sx={{ mt: 1, py: 0.35 }}
            data-testid="employer-operator-relocated-alert"
          >
            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
              Operator relocated pin
            </Typography>
            <Typography variant="caption" sx={{ lineHeight: 1.4 }}>
              Map views below are for the searched address, not the matched employer record. Reset to
              employer address before using this as decision evidence.
            </Typography>
          </Alert>
        ) : null}

        {searchCandidates.length > 0 ? (
          <List
            dense
            sx={{
              mt: 1,
              maxWidth: 640,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              maxHeight: 220,
              overflowY: 'auto',
            }}
            data-testid="employer-search-candidates"
          >
            <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, pt: 1, display: 'block' }}>
              Multiple matches — pick the correct address:
            </Typography>
            {searchCandidates.map((candidate) => {
              const meters = distanceMeters(building, candidate.location);
              return (
                <ListItemButton
                  key={candidate.placeId ?? candidate.formattedAddress}
                  onClick={() => applyCandidate(candidate)}
                >
                  <ListItemText
                    primary={candidate.formattedAddress}
                    secondary={`${formatMeters(meters)} from current pin · ${candidate.locationType}${
                      candidate.partialMatch ? ' · partial match' : ''
                    }`}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 700 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        ) : null}
      </Box>

      <Tabs
        value={stage}
        onChange={(_, next: StageKey) => setStage(next)}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ px: 1.5, borderBottom: '1px solid', borderColor: 'divider', minHeight: 40 }}
      >
        <Tab
          value="street"
          label="Street view"
          sx={{ textTransform: 'none', fontWeight: 700, minHeight: 40 }}
        />
        <Tab
          value="aerial"
          label="Satellite & top view"
          sx={{ textTransform: 'none', fontWeight: 700, minHeight: 40 }}
        />
        <Tab
          value="threeD"
          label="3D building"
          sx={{ textTransform: 'none', fontWeight: 700, minHeight: 40 }}
        />
      </Tabs>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          position: 'relative',
          bgcolor: 'action.hover',
        }}
      >
          {visited.includes('street') ? (
            <StageLayer visible={stage === 'street'}>
              <EmployerSiteStreetStage
                building={building}
                employerName={locationLabel}
                viewpoint={activeViewpoint}
                status={viewpointsStatus}
                coverageRadiusM={SITE_COVERAGE_RADIUS_M}
                lookupError={error}
              />
            </StageLayer>
          ) : null}

          {visited.includes('aerial') ? (
            <StageLayer visible={stage === 'aerial'}>
              <EmployerSiteAerialStage
                building={building}
                employerName={locationLabel}
                viewpoints={viewpointsForBuilding}
                activePanoId={activePanoId}
                onSelectViewpoint={openViewpoint}
              />
            </StageLayer>
          ) : null}

          {visited.includes('threeD') ? (
            <StageLayer visible={stage === 'threeD'}>
              <EmployerSite3DStage
                building={building}
                employerName={locationLabel}
                active={stage === 'threeD'}
                onUseAerialInstead={() => setStage('aerial')}
              />
            </StageLayer>
          ) : null}
      </Box>
    </Dialog>
  );
}

function StageLayer({ visible, children }: { visible: boolean; children: ReactNode }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        visibility: visible ? 'visible' : 'hidden',
        zIndex: visible ? 1 : 0,
      }}
      aria-hidden={!visible}
    >
      {children}
    </Box>
  );
}
